import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/shared/SectionHeader";

const Legal = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="py-12 md:py-16 bg-primary-light">
        <div className="container-wide">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground mb-4">
              Legal & Privacy
            </h1>
            <p className="text-lg text-muted-foreground">
              Important information about how we operate, handle your data, and protect your privacy.
            </p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="section-padding bg-background">
        <div className="container-narrow">
          {/* Privacy Policy */}
          <div id="privacy" className="mb-16 scroll-mt-24">
            <SectionHeader
              title="Privacy Policy"
              centered={false}
            />
            
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-4">
                Partnership Performance Lab is committed to protecting your privacy and handling 
                your personal data responsibly. This policy explains how we collect, use, and 
                protect your information.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Data We Collect
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>Contact information (name, email, phone number)</li>
                <li>Professional information (practice name, role, location)</li>
                <li>Assessment responses (for Inventory completion)</li>
                <li>Communication records (enquiries and correspondence)</li>
              </ul>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                How We Use Your Data
              </h3>
              <ul className="list-disc pl-6 space-y-2 mb-6">
                <li>To deliver our services and generate reports</li>
                <li>To respond to enquiries and provide support</li>
                <li>To send relevant updates (with your consent)</li>
                <li>To improve our services and user experience</li>
              </ul>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Confidentiality of Assessment Data
              </h3>
              <p className="mb-4">
                Individual assessment responses are treated with strict confidentiality. 
                Your personal responses are <strong>never</strong> shared with other partners, 
                your practice, or any third party. Partnership reports contain only aggregated 
                data that cannot identify individual respondents.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Research and Algorithm Development
              </h3>
              <p className="mb-4">
                By completing the GP Partnership Lab Inventory, you agree that your anonymised 
                responses may be used for research purposes and to improve the inventory algorithm. 
                This helps us provide better insights for future users. Your data is fully anonymised 
                before any research use—no personally identifiable information (name, email, or 
                practice details) is included in research datasets. You may withdraw this consent 
                at any time by contacting us.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Data Retention
              </h3>
              <p className="mb-4">
                We retain personal data only for as long as necessary to fulfil the purposes 
                for which it was collected, or as required by law. Assessment data is retained 
                for a maximum of 3 years unless you request earlier deletion.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Your Rights
              </h3>
              <p className="mb-4">
                Under UK GDPR, you have the right to access, correct, delete, or restrict 
                processing of your personal data. To exercise these rights, please contact us 
                at hello@partnershipperformancelab.co.uk.
              </p>
            </div>
          </div>

          {/* Terms of Service */}
          <div id="terms" className="mb-16 scroll-mt-24">
            <SectionHeader
              title="Terms of Service"
              centered={false}
            />
            
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-4">
                By using Partnership Performance Lab's services, you agree to these terms. 
                Please read them carefully.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Service Description
              </h3>
              <p className="mb-4">
                We provide leadership consultancy services including individual assessments, 
                partnership analysis, and facilitation for GP partnerships. Our services are 
                designed to support partnership performance, not to provide legal advice, 
                formal mediation, or clinical guidance.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Payment Terms
              </h3>
              <p className="mb-4">
                Payment is required in advance for individual assessments. For partnership 
                and facilitation services, payment terms are agreed during the discovery process. 
                Refunds are provided at our discretion.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Limitation of Liability
              </h3>
              <p className="mb-4">
                Our assessments and reports provide structured insight to inform decisions. 
                They do not constitute professional advice and should not be solely relied upon 
                for significant partnership decisions. We are not liable for decisions made 
                based on our reports or facilitation.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Intellectual Property
              </h3>
              <p className="mb-4">
                All assessment content, methodologies, and materials are the intellectual 
                property of Partnership Performance Lab. Reports generated for you may be 
                used within your practice but may not be resold or distributed commercially.
              </p>
            </div>
          </div>

          {/* Cookie Policy */}
          <div id="cookies" className="scroll-mt-24">
            <SectionHeader
              title="Cookie Policy"
              centered={false}
            />
            
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p className="mb-4">
                We use cookies to improve your experience on our website.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Essential Cookies
              </h3>
              <p className="mb-4">
                These cookies are necessary for the website to function properly. 
                They enable basic functions like page navigation and access to secure areas.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Analytics Cookies
              </h3>
              <p className="mb-4">
                We use analytics cookies to understand how visitors interact with our website. 
                This helps us improve our services. These cookies are only set with your consent.
              </p>

              <h3 className="font-display text-xl font-semibold text-foreground mt-8 mb-4">
                Managing Cookies
              </h3>
              <p className="mb-4">
                You can manage cookie preferences through your browser settings. Note that 
                disabling certain cookies may affect website functionality.
              </p>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Legal;
